import { faBuilding, faChevronRight } from "@fortawesome/pro-regular-svg-icons";

import {
	faChartPieSimple,
	faChartSimple,
	faCoins,
	faPhotoFilm,
	faSliders,
	faUsers,
	faVideo,
} from "@fortawesome/pro-solid-svg-icons";

import { useEffect, useState } from "react";

import i18n from "@/i18n";
import { ROUTE_CONFIG } from "@/routes/config";

import { Icon } from "@/components";
import { PermissionRoles } from "@/enum";

import { SvgIcon } from "@/components/Icon/SvgIcon";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";
import Clipboard from "@assets/icons/sidebar/clipboard.svg?react";
import Settings from "@assets/icons/sidebar/settings.svg?react";
import { useLazyCheckIfScenarioInProgressExistQuery } from "@/pages/Private/redux/course-progress/course-progress.api";
import { CourseProgressResponse } from "@/pages/Private/pages/Course/schema/course-progress";
import { hasPermission } from "@/utils";

export interface SidenavItem {
	title: string;
	path: string;
	onClick?: () => void;
	strictPath?: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	icon?: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	iconActive?: any;
	type?: string;
	permission?: PermissionRoles;
	companyIds?: number[];
	additionalTextClassName?: string;
	additionalContainerClassName?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	iconRight?: any;
	isHidden?: boolean;
}

interface NavItemInner {
	upper: SidenavItem[];
	middle?: SidenavItem[];
	secondMiddle?: SidenavItem[];
	lower: SidenavItem[];
}

interface NavItemConfig {
	ALL: NavItemInner;
}

export const useNavigationGetter = (): NavItemConfig => {
	const { user } = useAppSelector(authSelector);
	const [checkIfScenarioInProgressExist, { data }] = useLazyCheckIfScenarioInProgressExistQuery();
	const [currentActiveScenarioData, setCurrentActiveScenarioData] =
		useState<CourseProgressResponse | null>(null);

	useEffect(() => {
		if (data !== undefined) {
			setCurrentActiveScenarioData(data);
		}
	}, [data]);

	useEffect(() => {
		if (user && hasPermission(user, [PermissionRoles.USER])) {
			checkIfScenarioInProgressExist().catch(() => {
				setCurrentActiveScenarioData(null);
			});
		}
	}, [user, checkIfScenarioInProgressExist]);

	return {
		ALL: {
			upper: [
				{
					title: i18n.t("sidebar.courses"),
					path: ROUTE_CONFIG.COURSES,
					icon: <SvgIcon className="w-[24px] h-[24px] fill-primary-blue" svgIcon={Clipboard} />,
					iconActive: <SvgIcon className="w-[24px] h-[24px] fill-white" svgIcon={Clipboard} />,
					type: "icon",
					permission: PermissionRoles.COURSES,
				},
				{
					title: i18n.t("sidebar.analytics"),
					path: `${ROUTE_CONFIG.ANALYTICS}`,
					icon: <Icon className="w-[24px] h-[24px] text-primary-blue" icon={faChartPieSimple} />,
					iconActive: <Icon className="w-[24px] h-[24px] text-white" icon={faChartPieSimple} />,
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
				},
				{
					title: i18n.t("sidebar.tutorials"),
					path: ROUTE_CONFIG.TUTORIALS,
					icon: <Icon className="w-[24px] h-[24px] fill-primary-blue" icon={faVideo} />,
					iconActive: <Icon className="w-[24px] h-[24px] fill-white" icon={faVideo} />,
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
				},
				{
					title: i18n.t("sidebar.kpiDashboard"),
					path: ROUTE_CONFIG.KPI_DASHBOARD,
					icon: <Icon className="w-[24px] h-[24px] fill-primary-blue" icon={faChartSimple} />,
					iconActive: <Icon className="w-[24px] h-[24px] fill-white" icon={faChartSimple} />,
					type: "icon",
					permission: PermissionRoles.KPI_DASHBOARD,
				},
				{
					isHidden: !currentActiveScenarioData || currentActiveScenarioData?.id === undefined,
					title: i18n.t("sidebar.current-scenario"),
					path: `${ROUTE_CONFIG.COURSES}/${currentActiveScenarioData?.id}/progress`,
					iconRight: (
						<Icon className="w-[16px] h-[16px] text-primary-blue-hover" icon={faChevronRight} />
					),
					type: "icon",
					permission: PermissionRoles.USER,
					additionalTextClassName: "!text-primary-blue-hover !ml-0 mr-4 text-nowrap",
					additionalContainerClassName:
						"!bg-white !rounded-none border-t border-t-border-soft !mt-4 !pt-5",
				},
			],
			middle: [],
			lower: [
				{
					title: i18n.t("sidebar.settings"),
					path: ROUTE_CONFIG.ACCOUNT,
					icon: <SvgIcon className="w-[24px] h-[24px] fill-primary-blue" svgIcon={Settings} />,
					iconActive: <SvgIcon className="w-[24px] h-[24px] fill-white" svgIcon={Settings} />,
					type: "icon",
					permission: PermissionRoles.COURSES,
				},
				{
					title: i18n.t("sidebar.users"),
					path: `${ROUTE_CONFIG.COMPANIES}/${user?.company?.id}`,
					icon: <Icon className="w-[24px] h-[24px] text-primary-blue" icon={faUsers} />,
					iconActive: <Icon className="w-[24px] h-[24px] text-white" icon={faUsers} />,
					type: "icon",
					permission: PermissionRoles.UPDATE_USER,
				},
				{
					title: i18n.t("sidebar.mediaPresets"),
					path: ROUTE_CONFIG.MEDIA_PRESETS,
					icon: <Icon className="w-[24px] h-[24px] text-primary-blue" icon={faSliders} />,
					iconActive: <Icon className="w-[24px] h-[24px] text-white" icon={faSliders} />,
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
				},
				{
					title: i18n.t("sidebar.mediaLibrary"),
					path: ROUTE_CONFIG.MEDIA_LIBRARY,
					icon: <Icon className="w-[24px] h-[24px] text-primary-blue" icon={faPhotoFilm} />,
					iconActive: <Icon className="w-[24px] h-[24px] text-white" icon={faPhotoFilm} />,
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
				},
				{
					title: i18n.t("sidebar.mediaCosts"),
					path: ROUTE_CONFIG.MEDIA_COSTS,
					icon: <Icon className="w-[24px] h-[24px] text-primary-blue" icon={faCoins} />,
					iconActive: <Icon className="w-[24px] h-[24px] text-white" icon={faCoins} />,
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
				},
				{
					title: i18n.t("sidebar.companies"),
					path: ROUTE_CONFIG.COMPANIES,
					icon: <Icon className="w-[24px] h-[24px] text-primary-blue" icon={faBuilding} />,
					iconActive: <Icon className="w-[24px] h-[24px] text-white" icon={faBuilding} />,
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
				},
			],
		},
	};
};
